/**
 * DMARC alignment helpers.
 *
 * RFC 7489 §3.1 defines two alignment modes:
 *
 *   strict  — the auth identifier domain equals the From-header domain
 *             exactly (case-insensitive).
 *
 *   relaxed — the auth identifier domain shares the same Organizational
 *             Domain as the From-header domain. Org-domain is computed
 *             via the Public Suffix List: it's the first label *under*
 *             the registered suffix. So `mail.example.co.uk` and
 *             `bounce.example.co.uk` both have org-domain `example.co.uk`
 *             and are relaxed-aligned with each other.
 *
 * DMARC defaults to `r` (relaxed) for both `adkim` and `aspf` per
 * RFC 7489 §6.3.
 *
 * The previous in-app implementation used `domain.endsWith("." + parent)`
 * which:
 *   - missed cross-domain aliases (`googlemail.com` vs `google.com`),
 *   - mishandled multi-label TLDs (`mail.example.co.uk` got cut to
 *     `co.uk` when finding the parent), and
 *   - missed parent-domain alignment (monitoring `mail.example.com`
 *     when the signing domain is `example.com`).
 *
 * Powered by `tldts` which bundles the Mozilla PSL.
 */
import { getDomain } from "tldts";
/**
 * Extract the Organizational Domain from a hostname.
 *
 * Returns null if the input is not a valid public-suffix-rooted host
 * (private registry, IP literal, malformed input).
 *
 * @example
 *   getOrgDomain("mail.example.co.uk") // "example.co.uk"
 *   getOrgDomain("example.com")        // "example.com"
 *   getOrgDomain("a.b.c.example.com")  // "example.com"
 *   getOrgDomain("googlemail.com")     // "googlemail.com"
 *   getOrgDomain("invalid")            // null
 */
export function getOrgDomain(host) {
    if (!host)
        return null;
    const trimmed = host.trim().toLowerCase();
    if (!trimmed)
        return null;
    // tldts.getDomain returns null for IPs, hosts without public suffixes,
    // and malformed input — exactly what we want.
    return getDomain(trimmed) ?? null;
}
/**
 * Check whether two domains are aligned per RFC 7489 §3.1.
 *
 * - strict:  exact case-insensitive match.
 * - relaxed: same Organizational Domain (PSL-based).
 *
 * Returns false if either input is empty or has no resolvable org-domain.
 */
export function isAligned(authDomain, headerFromDomain, mode = "relaxed") {
    if (!authDomain || !headerFromDomain)
        return false;
    const a = authDomain.trim().toLowerCase();
    const b = headerFromDomain.trim().toLowerCase();
    if (!a || !b)
        return false;
    if (mode === "strict") {
        return a === b;
    }
    // Relaxed: compare org-domains. Fall back to exact match if either
    // side has no org-domain (private registry / non-PSL host) — this is
    // safer than reporting "not aligned" when both sides are the same
    // private domain.
    const orgA = getOrgDomain(a);
    const orgB = getOrgDomain(b);
    if (orgA && orgB)
        return orgA === orgB;
    return a === b;
}
/**
 * From a list of DKIM auth results in an aggregate report, pick the one
 * whose signing domain is aligned with the message's header-from domain.
 *
 * Returns null if no aligned signature exists. Use this — not the first
 * element of the array — when populating the legacy single-value
 * `dkim_domain` / `dkim_selector` columns. Picking [0] silently stores
 * the wrong selector when a third-party signer (e.g. `d=mailgun.org`)
 * comes before the customer's aligned signature in the report.
 */
export function pickAlignedDkim(results, headerFromDomain, mode = "relaxed") {
    if (!results || results.length === 0)
        return null;
    // Prefer aligned + pass; fall back to aligned + any.
    let alignedAny = null;
    for (const r of results) {
        if (!isAligned(r.domain, headerFromDomain, mode))
            continue;
        if (r.result === "pass")
            return r;
        if (!alignedAny)
            alignedAny = r;
    }
    return alignedAny;
}
/**
 * From a list of SPF auth results, pick the aligned one. DMARC alignment
 * uses the smtp.mailfrom (RFC5321.MailFrom) domain — never `helo`. Per
 * RFC 7489 §3.1.2, only `scope === "mfrom"` results count toward
 * alignment. This function filters helo-only results out for you.
 */
export function pickAlignedSpf(results, headerFromDomain, mode = "relaxed") {
    if (!results || results.length === 0)
        return null;
    let alignedAny = null;
    for (const r of results) {
        if (r.scope && r.scope !== "mfrom")
            continue;
        if (!isAligned(r.domain, headerFromDomain, mode))
            continue;
        if (r.result === "pass")
            return r;
        if (!alignedAny)
            alignedAny = r;
    }
    return alignedAny;
}
//# sourceMappingURL=alignment.js.map